/**
 * Format an ISO-8601 timestamp for display in admin tables and dialogs.
 *
 * Returns a localized "Mon DD, YYYY, HH:MM" string. Empty / unparseable input
 * yields an em-dash so the table cell never collapses to nothing.
 */
export function formatTimestamp(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Format an ISO-8601 timestamp as a compact age ("just now", "2 hr", "3 wk").
 *
 * For columns where recency matters more than the exact date — a "last login"
 * cell is scanned for who is still active, not for when precisely they were.
 * `now` is a parameter so the output is deterministic under test. Returns an
 * em-dash for missing or unparseable input; callers wanting a domain-specific
 * sentinel (such as "never") should branch before calling.
 */
export function formatRelativeAge(
  value: string | null | undefined,
  now: number = Date.now()
): string {
  if (!value) return '—';
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return '—';
  const seconds = Math.max(0, (now - parsed) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)} hr`;
  const days = Math.floor(seconds / 86_400);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'}`;
  if (days < 30) return `${Math.floor(days / 7)} wk`;
  if (days < 365) return `${Math.floor(days / 30)} mo`;
  return `${Math.floor(days / 365)} yr`;
}

/**
 * Format a `YYYY-MM-DD` day bucket as a short axis label ("Jul 29").
 *
 * Pinned to UTC so the label round-trips the exact year-month-day it was given.
 * Without the pin, a viewer west of UTC renders midnight-UTC as the previous
 * day and every bar is labelled one day early.
 */
export function formatDayLabel(day: string): string {
  const parsed = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return day;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  });
}

/**
 * Format a USD cost for admin cost displays. Small non-zero amounts keep
 * more decimals so a fraction of a cent is still visible; null yields "—".
 */
export function formatUsd(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  if (value === 0) return '$0';
  if (value < 0.01) return `$${value.toFixed(4)}`;
  if (value < 1) return `$${value.toFixed(3)}`;
  return `$${value.toFixed(2)}`;
}

/**
 * Format a token count compactly for badges and tables (1.2k, 3.4M, 1.1B).
 *
 * Null / NaN input yields an em-dash so layouts never collapse to nothing.
 */
export function formatTokenCount(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  const abs = Math.abs(value);
  if (abs < 1000) return String(value);
  if (abs < 1_000_000) return `${(value / 1000).toFixed(abs < 10_000 ? 1 : 0)}k`;
  if (abs < 1_000_000_000)
    return `${(value / 1_000_000).toFixed(abs < 10_000_000 ? 1 : 0)}M`;
  return `${(value / 1_000_000_000).toFixed(1)}B`;
}
